import type {
	FabConfig,
	NotificationsConfig,
	OnboardingBullet,
	OnboardingConfig,
	OnboardingTone,
	OnboardingTrigger,
	SessionReplayConfig,
	WidgetConfig,
} from "./types";

// Hard cap matched in the dashboard form. Belt-and-braces: if a corrupt
// or hand-edited config ships a wild value, the rolling buffer still
// stays within a sane bound.
const REPLAY_MIN_SECONDS = 10;
const REPLAY_MAX_SECONDS = 600;

// Matches the dashboard-side cap in `@weloop/core/widget`.
const MARGIN_MAX_PX = 800;

const DEFAULT_FAB: FabConfig = {
	// White bubble carrying the multicolor Weloop brand mark. Orgs override
	// the colors from the dashboard.
	backgroundColor: "#ffffff",
	draggable: false,
	foregroundColor: "#14141f",
	// The empty string selects the bundled Weloop logo SVG (see fab.ts) –
	// a JSON config can only carry text glyphs, not markup.
	icon: "",
	label: "Feedback",
	marginPx: 20,
	mode: "bubble",
	position: "right",
};

const DEFAULT_NOTIFICATIONS: NotificationsConfig = {
	badge: "count",
	previews: true,
};

export const DEFAULTS = {
	fab: DEFAULT_FAB,
	notifications: DEFAULT_NOTIFICATIONS,
};

const sleep = async (ms: number): Promise<void> => {
	// oxlint-disable-next-line promise/avoid-new -- no `setTimeout` promise helper exists in the browser, and we don't bundle a polyfill
	await new Promise<void>((resolve) => {
		setTimeout(resolve, ms);
	});
};

const readString = (raw: unknown): string | null =>
	typeof raw === "string" && raw !== "" ? raw : null;

const readPosition = (raw: unknown): FabConfig["position"] | null =>
	raw === "left" || raw === "right" ? raw : null;

const readMode = (raw: unknown): FabConfig["mode"] | null =>
	raw === "bubble" || raw === "vertical-tab" || raw === "horizontal-tab"
		? raw
		: null;

const readMargin = (raw: unknown): number | null =>
	typeof raw === "number" && Number.isFinite(raw)
		? Math.min(MARGIN_MAX_PX, Math.max(0, Math.floor(raw)))
		: null;

// Only http(s) URLs may reach the icon <img> – a corrupt config cannot ship
// a javascript: or data: source into the host page.
const readIconImageUrl = (raw: unknown): string | undefined => {
	if (typeof raw !== "string") {
		return undefined;
	}
	return raw.startsWith("https://") || raw.startsWith("http://")
		? raw
		: undefined;
};

const asObject = (raw: unknown): Readonly<Record<string, unknown>> | null =>
	typeof raw === "object" && raw !== null
		? // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- narrowing arbitrary JSON to indexable record after typeof === "object" guard
			(raw as Readonly<Record<string, unknown>>)
		: null;

const readFab = (raw: unknown): FabConfig => {
	const obj = asObject(raw);
	if (obj === null) {
		return DEFAULT_FAB;
	}
	const position = readPosition(obj["position"]) ?? DEFAULT_FAB.position;
	const iconImageUrl = readIconImageUrl(obj["iconImageUrl"]);
	return {
		backgroundColor:
			readString(obj["backgroundColor"]) ?? DEFAULT_FAB.backgroundColor,
		draggable: obj["draggable"] === true,
		foregroundColor:
			readString(obj["foregroundColor"]) ?? DEFAULT_FAB.foregroundColor,
		icon: readString(obj["icon"]) ?? DEFAULT_FAB.icon,
		...(iconImageUrl === undefined ? {} : { iconImageUrl }),
		label: readString(obj["label"]) ?? DEFAULT_FAB.label,
		marginPx: readMargin(obj["marginPx"]) ?? DEFAULT_FAB.marginPx,
		mode: readMode(obj["mode"]) ?? DEFAULT_FAB.mode,
		position,
	};
};

const readBadge = (raw: unknown): NotificationsConfig["badge"] | null =>
	raw === "count" || raw === "dot" || raw === "off" ? raw : null;

const readNotifications = (raw: unknown): NotificationsConfig => {
	const obj = asObject(raw);
	if (obj === null) {
		return DEFAULT_NOTIFICATIONS;
	}
	return {
		badge: readBadge(obj["badge"]) ?? DEFAULT_NOTIFICATIONS.badge,
		previews: obj["previews"] !== false,
	};
};

const readTrigger = (raw: unknown): OnboardingTrigger | null =>
	raw === "first-visit" || raw === "hover" || raw === "off" ? raw : null;

// Configs published before the classifications carried a home chip tone.
const LEGACY_ONBOARDING_TONES: Readonly<Record<string, OnboardingTone>> = {
	idea: "feature",
	question: "support",
};

export const readOnboardingTone = (raw: unknown): OnboardingTone => {
	if (
		raw === "bug" ||
		raw === "business" ||
		raw === "feature" ||
		raw === "it" ||
		raw === "support"
	) {
		return raw;
	}
	return typeof raw === "string"
		? (LEGACY_ONBOARDING_TONES[raw] ?? "business")
		: "business";
};

// Bullets past the contract's cap are dropped rather than rendered: a card
// taller than the launcher it points at reads as an interruption.
const ONBOARDING_BULLETS_MAX = 4;

const readBullets = (raw: unknown): OnboardingBullet[] => {
	if (!Array.isArray(raw)) {
		return [];
	}
	const bullets: OnboardingBullet[] = [];
	for (const entry of raw) {
		const obj = asObject(entry);
		const label = readString(obj?.["label"]);
		if (label !== null) {
			bullets.push({ label, tone: readOnboardingTone(obj?.["tone"]) });
		}
		if (bullets.length === ONBOARDING_BULLETS_MAX) {
			break;
		}
	}
	return bullets;
};

/**
 * The intro card's configuration, or undefined when there is nothing to show.
 * A missing title means no card: bullets under no heading explain nothing.
 */
const readOnboarding = (raw: unknown): OnboardingConfig | undefined => {
	const obj = asObject(raw);
	if (obj === null) {
		return undefined;
	}
	const trigger = readTrigger(obj["trigger"]);
	const title = readString(obj["title"]);
	if (trigger === null || trigger === "off" || title === null) {
		return undefined;
	}
	return {
		brandColor: readString(obj["brandColor"]),
		copy: {
			bullets: readBullets(obj["bullets"]),
			ctaLabel: readString(obj["ctaLabel"]),
			lang: readString(obj["lang"]),
			message: readString(obj["message"]),
			title,
		},
		projectName: readString(obj["projectName"]),
		trigger,
	};
};

// Only a six-digit hex reaches the host page's styles – the publisher writes
// one, and a hand-edited value must never become an arbitrary CSS token.
const readTheme = (raw: unknown): string | undefined => {
	const color = asObject(raw)?.["primaryColor"];
	return typeof color === "string" && /^#[0-9a-f]{6}$/iu.test(color)
		? color
		: undefined;
};

const readSessionReplay = (raw: unknown): SessionReplayConfig | undefined => {
	const obj = asObject(raw);
	if (obj === null) {
		return undefined;
	}
	if (obj["enabled"] !== true) {
		return undefined;
	}
	const durationRaw = obj["durationSeconds"];
	const duration = typeof durationRaw === "number" ? durationRaw : 60;
	const clamped = Math.min(
		REPLAY_MAX_SECONDS,
		Math.max(REPLAY_MIN_SECONDS, Math.floor(duration)),
	);
	return { durationSeconds: clamped, enabled: true };
};

// Browsers compare `event.origin` as a bare origin, so a self-hosted
// `WIDGET_PUBLIC_URL` published with a trailing slash or a path would drop
// every message from the widget. Anything that is not an http(s) URL is
// rejected with the config.
const readOrigin = (raw: unknown): string | null => {
	try {
		const url = new URL(String(raw));
		return /^https?:$/u.test(url.protocol) ? url.origin : null;
	} catch {
		return null;
	}
};

// Handwritten guard – keeps zod out of the runtime bundle. Missing optional
// fields fall back to baked-in defaults; the only hard requirements are the
// org + project slugs (used to build the iframe URL) and the widget origin
// (used as both iframe `src` origin and `postMessage` target). Exported for
// the browser extension, which fetches the JSON itself and hands the parsed
// config to `bootstrap()`.
export const parseConfig = (raw: unknown): WidgetConfig | null => {
	const obj = asObject(raw);
	if (obj === null) {
		return null;
	}
	const orgSlug = readString(obj["orgSlug"]);
	const projectSlug = readString(obj["projectSlug"]);
	const widgetOrigin = readOrigin(obj["widgetOrigin"]);
	if (orgSlug === null || projectSlug === null || widgetOrigin === null) {
		return null;
	}
	const onboarding = readOnboarding(obj["onboarding"]);
	const primaryColor = readTheme(obj["theme"]);
	return {
		fab: readFab(obj["fab"]),
		locale: readString(obj["locale"]) ?? undefined,
		notifications: readNotifications(obj["notifications"]),
		...(onboarding === undefined ? {} : { onboarding }),
		orgSlug,
		...(primaryColor === undefined ? {} : { primaryColor }),
		projectSlug,
		sessionReplay: readSessionReplay(obj["sessionReplay"]),
		widgetOrigin,
	};
};

const FETCH_TIMEOUT_MS = 10_000;

// Each reason a config cannot be used is logged once: a widget that silently
// never appears is the hardest failure for an integrator to diagnose.
const fail = (url: string, reason: string): null => {
	console.error(`[weloop] config ${url}: ${reason}`);
	return null;
};

const tryFetch = async (url: string): Promise<unknown> => {
	for (let attempt = 0; attempt < 2; attempt += 1) {
		try {
			const response = await fetch(url, {
				// `no-cache` revalidates on every boot so a republished config
				// takes effect on next load; `force-cache` on the retry serves
				// the stored copy without touching the network, so a visitor
				// whose network just failed still boots. Cache policy details
				// live in `infra/bunny-setup.md`.
				cache: attempt === 0 ? "no-cache" : "force-cache",
				credentials: "omit",
				headers: { Accept: "application/json" },
				// Bound the revalidation round trip – a stalled connection must
				// fall through to the cached retry, not hang the boot.
				signal:
					typeof AbortSignal.timeout === "function"
						? AbortSignal.timeout(FETCH_TIMEOUT_MS)
						: undefined,
			});
			if (response.status === 404) {
				console.error(
					`[weloop] unknown projectId – config not found at ${url}`,
				);
				return null;
			}
			if (response.status >= 500 && attempt === 0) {
				await sleep(500);
				continue;
			}
			if (!response.ok) {
				return fail(url, `HTTP ${response.status}`);
			}
			try {
				return (await response.json()) as unknown;
			} catch {
				return fail(url, "invalid JSON");
			}
		} catch {
			if (attempt === 0) {
				await sleep(500);
				continue;
			}
			return fail(url, "fetch failed (network or CSP)");
		}
	}
	return null;
};

// Returns a config object when a valid one is fetched, or `null` to abort
// boot (404 or otherwise unrecoverable). Never throws into host page code.
export const fetchConfig = async (
	configUrl: string,
): Promise<WidgetConfig | null> => {
	const result = await tryFetch(configUrl);
	if (result === null) {
		return null;
	}
	return (
		parseConfig(result) ??
		fail(configUrl, "orgSlug, projectSlug or widgetOrigin invalid")
	);
};
